import React from 'react';

import { Box } from 'ui/box';
import { Stack } from 'ui/stack';

import InsightScreenshot, {
  type InsightScreenshotComment,
} from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/insight-screenshot';
import PdfContextData from 'containers/admin/clients/client/tabs-components/insights-tab/insight-details/components/context-data-panel/pdf-context-data';

import { formatDate } from 'helpers/dates-formatter';

import type { InsightDto } from 'api/generated/schemas/insightDto';
import type { UserInsightDto } from 'api/generated/schemas/userInsightDto';

import Icons from 'assets/icons';

export type PdfContentInsight = InsightDto | UserInsightDto;

export interface PdfContentProps {
  insight: PdfContentInsight;
  comments?: InsightScreenshotComment[];
  showHeader?: boolean;
  showViewReport?: boolean;
}

const PdfContent = React.forwardRef<HTMLDivElement, PdfContentProps>(
  (
    { insight, comments = [], showHeader = true, showViewReport = false },
    ref
  ) => (
    <Box ref={ref} sx={{ bgcolor: 'common.white', p: 10, width: '1200px' }}>
      {showHeader && (
        <>
          <Icons.LoginLogo className="h-20 w-60 mb-5" />

          <Box
            sx={{
              mb: 4,
              pb: 3,
              borderBottomWidth: '1px',
              borderColor: 'tokens.gray.200',
            }}
          >
            <h1 className="text-4xl font-bold text-navy-800 mb-4">
              {insight.name}
            </h1>
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                color: 'tokens.gray.700',
                columnGap: 3,
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                <Icons.Calendar className="size-5" />
                <Box sx={{ fontWeight: 600 }}>
                  {formatDate(insight.createdDate, 'MMM D, YYYY')}
                </Box>
              </Stack>
              <Box>
                <span className="text-gray-600 text-sm">Created by: </span>
                <span className="text-gray-700 text-sm font-semibold">
                  {insight.createdBy || 'Unknown'}
                </span>
              </Box>
            </Stack>
          </Box>

          {insight?.description && (
            <Box
              sx={{
                bgcolor: 'tokens.blue.50',
                borderWidth: '1px',
                borderColor: 'tokens.blue.300',
                borderRadius: '0.375rem',
                p: 2.5,
                mb: 4,
              }}
            >
              <Stack
                direction="row"
                sx={{ alignItems: 'center', gap: 1, mb: 1 }}
              >
                <Icons.Idea customClass="size-5 text-blue-700" />
                <Box
                  sx={{
                    fontSize: '1.25rem',
                    lineHeight: '1.75rem',
                    fontWeight: 600,
                    color: 'tokens.blue.700',
                  }}
                >
                  Summary
                </Box>
              </Stack>
              <p className="text-gray-700">{insight.description}</p>
            </Box>
          )}
        </>
      )}

      {insight?.imageUrl && (
        <Box sx={{ mb: 4 }}>
          <Stack
            direction="row"
            sx={{
              justifyContent: 'center',
              alignItems: 'center',
              borderWidth: '1px',
              borderColor: 'tokens.gray.200',
              borderRadius: '0.5rem',
              bgcolor: 'common.white',
              mx: 'auto',
              maxWidth: '800px',
            }}
          >
            <InsightScreenshot
              imageUrl={insight.imageUrl}
              comments={comments}
              selectedCommentId={null}
              onCommentClick={() => {}}
            />
          </Stack>
        </Box>
      )}

      {comments.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <div className="space-y-4">
            {comments.map((comment, index) => {
              const commentNumber = index + 1;

              return (
                <Stack
                  key={comment.id}
                  direction="row"
                  sx={{ alignItems: 'flex-start', gap: 1.5 }}
                >
                  <Stack
                    direction="row"
                    sx={{
                      width: '2rem',
                      height: '2rem',
                      borderRadius: '9999px',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'common.white',
                      fontWeight: 700,
                      bgcolor: 'tokens.navy.400',
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    {commentNumber}
                  </Stack>
                  <p className="text-gray-700 flex-1 leading-relaxed pt-1.5">
                    {comment.message}
                  </p>
                </Stack>
              );
            })}
          </div>
        </Box>
      )}

      <PdfContextData insight={insight} showViewReport={showViewReport} />
    </Box>
  )
);

PdfContent.displayName = 'PdfContent';

export default PdfContent;
